import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar,
  Gift,
  Package,
  FileText, 
  Bell,
  MessageCircle,
  Users, 
  Building2,
  BarChart3,
  Heart,
  Shield,
  Search,
  Settings,
  DollarSign,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { logout } from '../../lib/auth.js';
import { hasCapability, isAdmin } from '../../lib/rbac.js';

function Sidebar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      show: true,
    },
    {
      name: 'Appointments',
      href: '/appointments',
      icon: Calendar,
      show: true,
    },
    {
      name: 'Holidays',
      href: '/holidays',
      icon: Gift,
      show: true,
    },
    {
      name: 'Orders',
      href: '/orders',
      icon: Package,

      show: true,
    },
    {
      name: 'Forms',
      href: '/forms',
      icon: FileText,
      show: true,
    },
    {
      name: 'Circulars',
      href: '/circulars',
      icon: FileText,
      show: true,
    },
    {
      name: 'Government Decisions',
      href: '/government-decisions',
      icon: FileText,
      show: true,
    },
    {
      name: 'Salary & Finance',
      href: '/salary-finance',
      icon: DollarSign,
      show: true,
    },
    {
      name: 'Form 16',
      href: '/form16',
      icon: FileText,
      show: true,
    },
    {
      name: 'Campaigns',
      href: '/whatsapp',
      icon: MessageCircle,
      show: true,
    },
    {
      name: 'WhatsApp Inside',
      href: '/whatsapp/inside',
      icon: MessageCircle,
      show: true,
    },
    {
      name: 'Users',
      href: '/users',
      icon: Users,
      show: true,
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: Settings,
      show: true,
    },
  ];


  const handleLogout = () => {
    logout();
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-md bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700"
        >
          {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-40 bg-teal-700 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:h-full
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isCollapsed ? 'w-16' : 'w-64'}
      `}>
        {/* Logo */}
        <div className={`flex items-center h-16 px-4 border-b border-gray-200 dark:border-gray-700 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
          {/* Logo on the left */}
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              {/* <span className="text-white font-bold text-sm">SRPF</span> */}
            </div>
            {!isCollapsed && (
              <span className="text-lg font-semibold text-gray-100 dark:text-white">
                Dashboard
              </span>
            )}
          </div>
          
          {/* Collapse toggle button */}
          {!isCollapsed && (
            <button
              onClick={toggleCollapse}
              className="p-1 px-2 text-white hover:text-gray-300 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-white/10 rounded transition-colors duration-200"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          
          {/* When collapsed, show expand button centered */}
          {isCollapsed && (
            <button
              onClick={toggleCollapse}
              className="p-1 px-2 text-white hover:text-gray-300 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-white/10 rounded transition-colors duration-200"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </div>

        

        {/* Navigation */}
        <nav className="mt-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <div className="space-y-1">
            {navigation.filter(item => item.show).map((item) => {
              const hasSubmenu = item.submenu && item.submenu.length > 0;
              const isSubmenuOpen = openSubmenu === item.name;
              const isActive = location.pathname === item.href || (hasSubmenu && item.submenu.some(sub => location.pathname === sub.href));
              
              return (
                <div key={item.name}>
                  {hasSubmenu ? (
                    <>
                      <div className={`
                        group flex items-center justify-between gap-x-3 px-3 py-2 text-sm font-medium transition-all duration-200
                        ${isActive 
                          ? 'bg-teal-300 text-gray-900' 
                          : 'text-white dark:text-gray-300 hover:bg-teal-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                        }
                      `}>
                        <NavLink
                          to={item.href}
                          className="flex items-center gap-x-3 flex-1"
                          onClick={() => setIsMobileMenuOpen(false)}
                          title={isCollapsed ? item.name : ''}
                        >
                          <item.icon 
                            className={`
                              transition-colors duration-200 flex-shrink-0
                              ${isActive 
                                ? 'text-teal-700' 
                                : 'text-white group-hover:text-gray-500 dark:group-hover:text-gray-300'
                              }
                              ${isCollapsed ? 'mx-auto h-5 w-5' : 'h-5 w-5'}
                            `}
                          />
                          {!isCollapsed && (
                            <span className="truncate">{item.name}</span>
                          )}
                        </NavLink>
                        {!isCollapsed && hasSubmenu && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setOpenSubmenu(isSubmenuOpen ? null : item.name);
                            }}
                            className="p-1 hover:bg-white/10 rounded transition-colors"
                            title={isSubmenuOpen ? 'Collapse' : 'Expand'}
                          >
                            {isSubmenuOpen ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                      {!isCollapsed && isSubmenuOpen && hasSubmenu && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.submenu.map((subItem) => {
                            const isSubActive = location.pathname === subItem.href;
                            return (
                              <NavLink
                                key={subItem.name}
                                to={subItem.href}
                                className={`
                                  flex items-center gap-x-3 px-3 py-2 text-sm font-medium transition-all duration-200 rounded
                                  ${isSubActive 
                                    ? 'bg-teal-400 text-gray-900' 
                                    : 'text-white/80 dark:text-gray-300 hover:bg-teal-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                                  }
                                `}
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                <span className="truncate">{subItem.name}</span>
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <NavLink
                      to={item.href}
                      end={item.href === '/whatsapp'}
                      className={({ isActive }) => `
                        group flex items-center gap-x-3 px-3 py-2 text-sm font-medium transition-all duration-200
                        ${isActive 
                          ? 'bg-teal-300 text-gray-900  border-teal-700' 
                          : 'text-white dark:text-gray-300 hover:bg-teal-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                        }
                      `}
                      onClick={() => setIsMobileMenuOpen(false)}
                      title={isCollapsed ? item.name : ''}
                    >
                      <item.icon 
                        className={({ isActive }) => `
                          transition-colors duration-200 flex-shrink-0
                          ${isActive 
                            ? 'text-teal-700' 
                            : 'text-white group-hover:text-gray-500 dark:group-hover:text-gray-300'
                          }
                          ${isCollapsed ? 'mx-auto h-5 w-5' : 'h-5 w-5'}
                        `}
                      />
                      {!isCollapsed && (
                        <span className="truncate">{item.name}</span>
                      )}
                    </NavLink>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleLogout}
            className={`
              flex items-center w-full  py-4 text-sm font-medium text-white dark:text-gray-300 hover:bg-teal-300 dark:hover:bg-gray-700 transition-all duration-200
              ${isCollapsed ? 'justify-center' : ''}
            `}
            title={isCollapsed ? 'Logout' : ''}
          >
            <LogOut className={`h-5 w-5 text-white ${isCollapsed ? 'mx-auto' : 'mr-3'}`} />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
}

export default Sidebar;
